/**
 * editing operations
 * 
 * @author    Jens Gruschel, Maurizio Tidei
 * @copyright © 2018 contexagon GmbH
 */


import {openPageAction} from "../redux/view/actions";
import {
    MEDIA_UPLOAD_PAGE,
    OBJECT_EDIT_PAGE,
    OBJECT_LIST_PAGE
} from "../redux/view/consts";
import {pushStateAction} from "../redux/navigation/actions";
import {showCurrentFilter} from "./listing";
import {
    REST_CALL_LOAD_OBJECT,
    REST_CALL_LOAD_ALL_TAGS,
    REST_CALL_SAVE_OBJECT,
    REST_SERVER_BASE_URL
} from "../config";
import {showMessageAction, showDialogMessageAction} from "../redux/message/actions";
import {showError} from "./message";
import {
    popEditStackAction,
    assignElementAction,
    addTagAction,
    changeTabAction,
    createNewObjectAction,
    createNewObjectReferenceAction,
    createNewElementAction,
    deleteTagAction,
    loadObjectAction,
    pushElementAction,
    saveObjectSuccessAction,
    switchObjectAction,
    loadAllTagsSuccessAction,
    fetchReferencedObjectsAction,
    fetchReferencableObjectsAction,
    updateObjectMapAction
} from "../redux/editing/actions";
import {fetchJSONActivity, showJSONResponse} from "./communication";
import {fetchObjectList, excludeDeleted} from "./objects";
import {requestNextSequenceValues} from "./sequences";
import {selectReferencedObjectIds} from '../redux/types/selectors';
import {selectObjectById} from "../redux/editing/selectors";
import {selectObjectTitle, selectFieldsWithDefaultValues, selectFieldValue, selectSequenceFieldDefs} from "../redux/types/selectors";
import {selectTranslation} from "../redux/configuration/selectors";
import {ACCESS_WRITABLE} from "../redux/authorization/consts";


export function createNewObject(typeId) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("create new object"));

        // create new object
        const fields = await getPredefinedFields(getState(), typeId);
        dispatch(createNewObjectAction(typeId, fields));

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function createNewMediaItem() {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("create new media item"));

        // create new media item
        const fields = await getPredefinedFields(getState(), "mediaitem");
        dispatch(createNewObjectAction("mediaitem", fields));

        // load existing media items
        await fetchReferencableObjects("mediaitem", {})(dispatch, getState);

        // open page
        dispatch(openPageAction(MEDIA_UPLOAD_PAGE));
    }

}

export function selectReference(fieldPath, itemTypeId, constraint, flags = {}) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("select reference"));

        // load existing objects of the given type
        await fetchReferencableObjects(itemTypeId, constraint)(dispatch, getState);

        // create new object reference
        const fields = await getPredefinedFields(getState(), itemTypeId);
        dispatch(createNewObjectReferenceAction(fieldPath, itemTypeId, fields, flags));

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function addElement(fieldPath, itemTypeId, fields = {}) {

    return (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("add element"));

        // create new element item
        const defaultFields = selectFieldsWithDefaultValues(getState().types, itemTypeId);
        dispatch(createNewElementAction(fieldPath, itemTypeId, {...defaultFields, ...fields}));

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function refreshObjectReferences(object) {

    return async (dispatch, getState) => {
        loadReferencedObjects(dispatch, getState, object);
    }
}

export function editObject(objectId) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("edit object", "?object=" + objectId));

        // load object
        await loadObject(dispatch, getState, objectId, {clearStack: true});

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function pushAndEditObject(objectId, readOnly) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("edit object", "?object=" + objectId));

        // load object
        await loadObject(dispatch, getState, objectId, {clearStack: false, readOnly: readOnly});

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function pushAndEditElement(fieldPath, element, readOnly) {

    return (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("edit element"));

        // extract element
        dispatch(pushElementAction(fieldPath, element, {readOnly: readOnly}));

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function duplicateObject(objectId) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("duplicate object"));

        try {
            const { authorization, configuration } = getState();
            const token = authorization.accessToken;
            const translate = (value) => selectTranslation(configuration, value);

            // load object to check its type
            const response = await fetchJSONActivity(dispatch, "loading object",
                REST_SERVER_BASE_URL + REST_CALL_LOAD_OBJECT.path.replace("<id>", objectId), {
                    method: REST_CALL_LOAD_OBJECT.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + token
                    }
                }
            );

            const loadedObject = response.data;

            if (loadedObject.type === 'cbox') {
                // deep copy: also duplicate referenced menu frames
                await deepDuplicateCBox(dispatch, getState, loadedObject, translate);
            } else {
                // regular clone: strip metadata, append "(copy)", load into Redux state (unsaved)
                delete loadedObject.id;
                delete loadedObject['objectaccess'];
                delete loadedObject['user-created'];
                delete loadedObject['user-modified'];
                delete loadedObject['timestamp-created'];
                delete loadedObject['timestamp-modified'];

                for (const fieldName of ['title', 'name']) {
                    if (fieldName in loadedObject.fields) {
                        loadedObject.fields[fieldName] = translate(loadedObject.fields[fieldName]) + " (copy)";
                        break;
                    }
                }

                dispatch(loadObjectAction(loadedObject, {
                    accessLevel: ACCESS_WRITABLE,
                    referencedBy: [],
                    fieldsetAccessLevels: response.fieldsetaccesslevels
                }, {clearStack: true}));
                await loadReferencedObjects(dispatch, getState, loadedObject);
                loadAllTags(dispatch, getState);
            }

            // open page
            dispatch(openPageAction(OBJECT_EDIT_PAGE));
        }
        catch (error) {
            showError(error)(dispatch);
        }
    }
}

/**
 * Generate a client-side object ID in the same format the server uses:
 * "<type>.XXXXXXXX-XXXX-4XXX-XXXX-XXXXXXXXXXXX" (UUID v4, uppercase)
 */
function generateObjectId(type) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant bits
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0').toUpperCase());
    const uuid = [
        hex.slice(0, 4).join(''),
        hex.slice(4, 6).join(''),
        hex.slice(6, 8).join(''),
        hex.slice(8, 10).join(''),
        hex.slice(10, 16).join('')
    ].join('-');
    return `${type}.${uuid}`;
}

/**
 * Load a menu frame, apply all idMap substitutions to its inline menuaction
 * and sceneaction references, then save it with a pre-generated ID in a
 * single server round-trip (no double-save needed).
 */
async function copyMenuFrameWithSubstitution(dispatch, token, originalMenuFrameId, newId, idMap, sceneCache) {

    const response = await fetchJSONActivity(dispatch, "loading menu frame for copy",
        REST_SERVER_BASE_URL + REST_CALL_LOAD_OBJECT.path.replace("<id>", originalMenuFrameId), {
            method: REST_CALL_LOAD_OBJECT.method,
            headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token }
        }
    );
    const copy = JSON.parse(JSON.stringify(response.data));

    // set pre-generated ID and strip metadata
    copy.id = newId;
    delete copy['objectaccess'];
    delete copy['user-created'];
    delete copy['user-modified'];
    delete copy['timestamp-created'];
    delete copy['timestamp-modified'];
    delete copy.title; // let the server regenerate the title from fields.name

    // append "(copy)" to name field
    if (copy.fields && typeof copy.fields.name === 'string') {
        copy.fields.name = copy.fields.name + " (copy)";
    }

    // collect all inline action arrays (additionalactions + each item's actions)
    const actionSources = [
        ...((copy.fields && copy.fields.additionalactions) || []),
        ...((copy.fields && copy.fields.items) || []).flatMap(
            item => (item.fields && item.fields.actions) || []
        )
    ];

    // substitute direct inline menuaction references
    substituteMenuIdsInActions(actionSources, idMap);

    // for sceneactions: copy the referenced scene with substituted menu IDs
    for (const action of actionSources) {
        if (action.type !== 'sceneaction') continue;
        const sceneRefs = (action.fields && action.fields.scene) || [];
        if (sceneRefs.length === 0) continue;
        const newSceneId = await copySceneWithSubstitution(dispatch, token, sceneRefs[0], idMap, sceneCache);
        if (newSceneId) action.fields.scene = [newSceneId];
    }

    // single save — server upserts because id is present
    await saveObjectToServer(dispatch, "saving menu frame copy", copy, token);
}

async function deepDuplicateCBox(dispatch, getState, originalCBox, translate) {

    const token = getState().authorization.accessToken;

    // deep clone and strip metadata
    const cboxCopy = JSON.parse(JSON.stringify(originalCBox));
    delete cboxCopy.id;
    delete cboxCopy['objectaccess'];
    delete cboxCopy['user-created'];
    delete cboxCopy['user-modified'];
    delete cboxCopy['timestamp-created'];
    delete cboxCopy['timestamp-modified'];
    delete cboxCopy.title; // let server regenerate

    // append "(copy)" to all name fields
    // fields.name is used by find_box() for Socket.IO room lookup — must always be updated
    for (const fieldName of ['title', 'name']) {
        if (fieldName in cboxCopy.fields) {
            cboxCopy.fields[fieldName] = translate(cboxCopy.fields[fieldName]) + " (copy)";
        }
    }

    // pre-generate IDs for menu frame copies so the full substitution map is
    // available before any save — eliminates the need for a second save pass
    const idMap = {};
    const menuARef = originalCBox.fields.menuA;
    let newMenuAId = null;
    if (menuARef && menuARef.length > 0) {
        newMenuAId = generateObjectId('menuframe');
        idMap[menuARef[0]] = newMenuAId;
        cboxCopy.fields.menuA = [newMenuAId];
    }
    const menuBRef = originalCBox.fields.menuB;
    let newMenuBId = null;
    if (menuBRef && menuBRef.length > 0) {
        newMenuBId = generateObjectId('menuframe');
        idMap[menuBRef[0]] = newMenuBId;
        cboxCopy.fields.menuB = [newMenuBId];
    }

    // copy each menu frame in a single save with all cross-references already substituted
    const sceneCache = {};
    if (newMenuAId) await copyMenuFrameWithSubstitution(dispatch, token, menuARef[0], newMenuAId, idMap, sceneCache);
    if (newMenuBId) await copyMenuFrameWithSubstitution(dispatch, token, menuBRef[0], newMenuBId, idMap, sceneCache);

    // save the CBox copy to get a server-assigned ID
    const savedCBoxResponse = await saveObjectToServer(dispatch, "saving CBox copy", cboxCopy, token);
    const savedCBox = savedCBoxResponse.data;

    // load the saved CBox into editing state (already saved, user can review and update)
    await loadObject(dispatch, getState, savedCBox.id, {clearStack: true});
}

/**
 * Load a scene, check if any of its actions (recursively) reference a menu frame
 * ID that is in idMap. If so, copy the scene with substituted IDs, save it and
 * return the new scene ID. Returns null if no substitution was needed.
 * Uses sceneCache to avoid copying the same scene twice.
 */
async function copySceneWithSubstitution(dispatch, token, sceneId, idMap, sceneCache) {

    if (sceneId in sceneCache) return sceneCache[sceneId];

    const sceneResp = await fetchJSONActivity(dispatch, "loading scene for ref fix",
        REST_SERVER_BASE_URL + REST_CALL_LOAD_OBJECT.path.replace("<id>", sceneId), {
            method: REST_CALL_LOAD_OBJECT.method,
            headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token }
        }
    );
    const scene = sceneResp.data;

    // work on a deep clone so we don't mutate the original
    const actionsCopy = JSON.parse(JSON.stringify((scene.fields && scene.fields.actions) || []));
    const changed = substituteMenuIdsInActions(actionsCopy, idMap);

    if (!changed) {
        sceneCache[sceneId] = null;
        return null;
    }

    // save a new scene copy with substituted menu IDs
    const sceneCopy = JSON.parse(JSON.stringify(scene));
    delete sceneCopy.id;
    delete sceneCopy['objectaccess'];
    delete sceneCopy['user-created'];
    delete sceneCopy['user-modified'];
    delete sceneCopy['timestamp-created'];
    delete sceneCopy['timestamp-modified'];
    sceneCopy.fields.actions = actionsCopy;

    const savedResp = await saveObjectToServer(dispatch, "saving scene copy for ref fix", sceneCopy, token);
    const newSceneId = savedResp.data.id;
    sceneCache[sceneId] = newSceneId;
    return newSceneId;
}

/**
 * Recursively walk an actions array and substitute any menuaction.fields.menu[0]
 * values found in idMap. Mutates the array in place and returns true if any
 * substitution was made.
 */
function substituteMenuIdsInActions(actions, idMap) {
    let changed = false;
    for (const action of actions) {
        if (!action || !action.fields) continue;
        if (action.type === 'menuaction') {
            const menu = action.fields.menu;
            if (menu && menu.length > 0 && idMap[menu[0]]) {
                action.fields.menu = [idMap[menu[0]]];
                changed = true;
            }
        }
        // recurse into nested inline action arrays (conditionalaction, caseaction)
        for (const nestedField of ['actions', 'alternative']) {
            if (action.fields[nestedField] && substituteMenuIdsInActions(action.fields[nestedField], idMap)) {
                changed = true;
            }
        }
        if (action.type === 'caseaction' && action.fields.cases) {
            for (const c of action.fields.cases) {
                if (c.fields && c.fields.actions && substituteMenuIdsInActions(c.fields.actions, idMap)) {
                    changed = true;
                }
            }
        }
    }
    return changed;
}

async function getPredefinedFields(state, objectTypeId) {

    const defaultFields = selectFieldsWithDefaultValues(state.types, objectTypeId);
    const sequenceFieldDefs = selectSequenceFieldDefs(state.types, objectTypeId);
    if (sequenceFieldDefs.length === 0) {
        return defaultFields;
    }
    else {
        const sequenceFields = await requestNextSequenceValues(
            sequenceFieldDefs,
            state.authorization.accessToken
        );
        return {...defaultFields, ...sequenceFields};
    }
}

async function saveObjectToServer(dispatch, description, object, token) {

    return await fetchJSONActivity(dispatch, description,
        REST_SERVER_BASE_URL + REST_CALL_SAVE_OBJECT.path, {
            method: REST_CALL_SAVE_OBJECT.method,
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            },
            body: JSON.stringify(object)
        }
    );
}

function completeObject(state, object) {
    const translate = (value) => selectTranslation(state.configuration, value);
    return {
        ...object,
        title: selectObjectTitle(state.types, state.editing.objects, object, translate)
    };
}

export function saveObject(object, close) {

    return async (dispatch, getState) => {

        const stackSize = getState().editing.editStack.length;
        console.log("saveObject", object);

        try {
            const state = getState();
        
            const json = await saveObjectToServer(dispatch, "saving object", completeObject(state, object), state.authorization.accessToken);
            const modifiedObject = json.data;
            const accessLevel = json.accesslevel;
            const newObjectId = modifiedObject ? modifiedObject.id : undefined;

            dispatch(saveObjectSuccessAction(modifiedObject));
            dispatch(showMessageAction("Speichern erfolgreich."));

            // close editing view or otherwise remain in current view
            if (close) {
                if (stackSize > 1) {
                    // dispatch(pushStateAction("???")); // TODO: push state?
                    dispatch(popEditStackAction());
                }
                else {
                    await showCurrentFilter()(dispatch, getState);
                    dispatch(pushStateAction("show objects"));
                    dispatch(openPageAction(OBJECT_LIST_PAGE));
                }
            }
            else if (newObjectId) {
                // object ID might have been changed by server
                dispatch(switchObjectAction(newObjectId, accessLevel || ACCESS_WRITABLE)) // TODO: remove ACCESS_WRITABLE if server really returns this value
            }
        }
        catch (error) {
            showError(error)(dispatch);
        }
    }
}

export function saveAndAssignReference(newObject) {

    return async (dispatch, getState) => {
        try {
            const state = getState();

            const json = await saveObjectToServer(dispatch, "saving object", completeObject(state, newObject), state.authorization.accessToken);
            const modifiedObject = json.data;
            const newObjectId = modifiedObject ? modifiedObject.id : undefined;

            dispatch(saveObjectSuccessAction(modifiedObject));
            if (newObjectId) {
                dispatch(assignElementAction(newObjectId));
                dispatch(updateObjectMapAction([modifiedObject]));
                dispatch(showMessageAction("Speichern und zuweisen erfolgreich."));
            }
            else {
                showError("could not attach object")(dispatch);
            }
        }
        catch (error) {
            showError(error)(dispatch);
        }
    }
}

export function assignElement(value) {

    return (dispatch, getState) => {
        const state = getState();
        dispatch(assignElementAction(completeObject(state, value)));
    }
}

export function cancelWithoutSaving() {

    return async (dispatch, getState) => {

        const stackSize = getState().editing.editStack.length;

        if (stackSize > 1) {
            // dispatch(pushStateAction("???")); // TODO: push state?
            dispatch(popEditStackAction());
        }
        else {
            await showCurrentFilter()(dispatch, getState);
            dispatch(pushStateAction("show objects"));
            dispatch(openPageAction(OBJECT_LIST_PAGE));
        }
    }
}

async function loadReferencedObjects(dispatch, getState, object) {
    let referencedObjectIds = selectReferencedObjectIds(getState().types, object);
    if (object.objectaccess) {
        if (object.objectaccess.readableFor) {
            referencedObjectIds = [...referencedObjectIds, ...object.objectaccess.readableFor];
        }
        if (object.objectaccess.writableFor) {
            referencedObjectIds = [...referencedObjectIds, ...object.objectaccess.writableFor];
        }
    }
    console.log("loading referenced objects", referencedObjectIds);
    if (referencedObjectIds.length > 0) {
        await fetchReferencedObjects(referencedObjectIds)(dispatch, getState);
    }
}

async function loadObject(dispatch, getState, id, params = {}) {

    try {
        const { authorization, configuration } = getState();
        const translate = (value) => selectTranslation(configuration, value);

        const response = await fetchJSONActivity(dispatch, "loading object",
            REST_SERVER_BASE_URL + REST_CALL_LOAD_OBJECT.path.replace("<id>", id), {
                method: REST_CALL_LOAD_OBJECT.method,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + authorization.accessToken
                }
            }
        );

        const loadedObject = response.data;
        const accessLevel = response.accesslevel;
        const fieldsetAccessLevels = response.fieldsetaccesslevels;
        const referencedBy = response.referencedBy;
        console.log("loaded object", loadedObject, "access level", accessLevel, "referenced by", referencedBy);

        if (params.clone === true) {

            // remove object sepcific information
            delete loadedObject.id;
            delete loadedObject['objectaccess']
            delete loadedObject['user-created']
            delete loadedObject['user-modified']
            delete loadedObject['timestamp-created']
            delete loadedObject['timestamp-modified']

            // add "(copy)" to title or name
            for (const fieldName in {title: '', name: ''}) {
                if (fieldName in loadedObject.fields) {
                    loadedObject.fields[fieldName] = translate(loadedObject.fields[fieldName]) + " (copy)";
                    break;
                }
            }

            dispatch(loadObjectAction(loadedObject, {
                accessLevel: ACCESS_WRITABLE,
                referencedBy: [],
                fieldsetAccessLevels: fieldsetAccessLevels
            }, params));    
        }
        else {

            dispatch(loadObjectAction(loadedObject, {
                accessLevel: accessLevel,
                referencedBy: referencedBy,
                fieldsetAccessLevels: fieldsetAccessLevels
            }, params));    
        }
    
        await loadReferencedObjects(dispatch, getState, loadedObject);

        // load all tags asynchronously (await not needed)
        // noinspection JSIgnoredPromiseFromCall
        loadAllTags(dispatch, getState);
    }
    catch (error) {
        showError(error)(dispatch);
    }
}

async function loadAllTags(dispatch, getState) {

    try {
        const { authorization } = getState();

        const json = await fetchJSONActivity(dispatch, "loading tags",
            REST_SERVER_BASE_URL + REST_CALL_LOAD_ALL_TAGS.path, {
                method: REST_CALL_LOAD_ALL_TAGS.method,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + authorization.accessToken
                }
            }
        );

        let loadedTags = json.tags;
        console.log("loaded tags:", loadedTags);

        dispatch(loadAllTagsSuccessAction(loadedTags));
    }
    catch (error) {
        showError(error)(dispatch);
    }
}

function fetchReferencableObjects(objectTypeId, constraint = {}) {
    return async (dispatch, getState) => {
        try {
            const objectList = await fetchObjectList(
                dispatch,
                getState,
                excludeDeleted(
                    {"type": objectTypeId, ...constraint},
                    {"timestamp-modified": -1}
                )
            );
            dispatch(fetchReferencableObjectsAction(objectList));
        }
        catch (error) {
            dispatch(fetchReferencableObjectsAction([]));
            showError(error)(dispatch);
        }
    }
};

function fetchReferencedObjects(objectIds) {
    return async (dispatch, getState) => {
        if (objectIds.length > 0) {
            try {
                // search for deleted and non-deleted objects, because otherwise references to deleted objects are invisible
                const objectList = await fetchObjectList(dispatch, getState, {"id": {"$in": objectIds}});
                dispatch(fetchReferencedObjectsAction(objectList));
            }
            catch (error) {
                showError(error)(dispatch);
            }
        }
    }
}

export function popAndAddReference(referenceObjectId) {
    return dispatch => {
        // TODO: push new state?
        // dispatch(pushStateAction("edit object"));
        dispatch(assignElementAction(referenceObjectId));
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function deleteTag(objectId, index) {

    return dispatch => {
        console.log("deleteTag, objectId:", objectId, "index:", index);
        dispatch(deleteTagAction(objectId, index));
    };
}

export function addTag(objectId, tag) {

    return dispatch => {
        console.log("addTag, objectId:", objectId, "tag:", tag);
        dispatch(addTagAction(objectId, tag));
    };
}

/**
 * change tab
 * @param objectId  object identifier
 * @param tabKey    key of the tab set
 * @param index     new tab index
 * @returns {Function}
 * @author          Jens Gruschel
 */
export function changeTab(tabKey, index) {
    return dispatch => {
        dispatch(changeTabAction(tabKey, index));
    }
}

/**
 * complete string by filling placeholders with current field values
 * @param state     state
 * @param object    object
 * @param template  string containing placeholders
 * @returns         {string}
 * @author          Maurizio Tidei, Jens Gruschel
 */
function completePlaceholders(state, object, template) {
    if (template.match(/^<[^>]+>$/)) {
        // single field? no uri encoding
        return selectFieldValue(state.types, state.editing.objects, object, template.slice(1, -1), false);
    }
    return template.replace(/<(.+?)>/g, (match, submatch) => {
        const fieldValue = selectFieldValue(state.types, state.editing.objects, object, submatch, false);
        if (fieldValue === undefined || fieldValue === null) return '';
        else if (typeof fieldValue === "object") return encodeURIComponent(JSON.stringify(fieldValue));
        else return encodeURIComponent(fieldValue);
    });
}

/**
 *
 * @param objectId
 * @param resturl
 * @param restmethod
 * @returns {Function}
 * @author Maurizio Tidei
 */
export function executeObjectCommand(objectId, resturl, restmethod, bodyDef, onFinish) {

    return async (dispatch, getState) => {

        console.log("executing command", restmethod, resturl);

        const state = getState();
        const object = selectObjectById(state.editing, objectId);

        // replace field names within URL by field values
        const resturlFilled = completePlaceholders(state, object, resturl);

        // provide field values as request body
        let bodyValue;
        if (typeof bodyDef === "string" && bodyDef.length > 0)
        {
            if (bodyDef.substr(0, 1) === "{" && bodyDef.substr(-1, 1) === "}") {
                // make an object with defined fields
                const parts = bodyDef.slice(1, -1).split(",").map(s => s.trim());
                bodyValue = {}
                parts.forEach(part => {
                    const colonIndex = part.indexOf(":");
                    const field = colonIndex >= 0 ? part.substr(colonIndex + 1).trim() : part.trim();
                    const name = colonIndex >= 0 ? part.substr(0, colonIndex).trim() : field;
                    bodyValue[name] = selectFieldValue(state.types, state.editing.objects, object, field, false);
                });
            }
            else if (bodyDef.substr(0, 1) === "[" && bodyDef.substr(-1, 1) === "]") {
                // make an array
                const parts = bodyDef.slice(1, -1).split(",").map(s => s.trim());
                bodyValue = parts.map(part => selectFieldValue(state.types, state.editing.objects, object, part), false);
            }
            else {
                // simply pass the given field
                bodyValue = selectFieldValue(state.types, state.editing.objects, object, bodyDef.trim(), false);
            }
        }

        try {
            const response = await fetchJSONActivity(dispatch, "executing command",
                REST_SERVER_BASE_URL + resturlFilled, {
                    method: restmethod,
                    body: bodyValue ? JSON.stringify(bodyValue) : undefined,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + state.authorization.accessToken
                    }
                }
            );
            showJSONResponse(dispatch, "command response", response);
        }
        catch (error) {
            if (onFinish) onFinish();
            console.log(error);
            dispatch(showDialogMessageAction("Error executing Command", error.message));
        }
        finally {
            if (onFinish) onFinish();
        }
    }
}

export function completeFrameURL(objectId, url) {
    return (dispatch, getState) => {

        const state = getState();
        const object = selectObjectById(state.editing, objectId);

        // replace field names within URL by field values
        // console.log("frame URL", url, completePlaceholders(state, object, url));
        return completePlaceholders(state, object, url);
    }
}

export function completeLabel(objectId, value) {
    return (dispatch, getState) => {

        const state = getState();
        const object = selectObjectById(state.editing, objectId);

        // replace field names within value by field values
        // console.log("label", value, completePlaceholders(state, object, value));
        return completePlaceholders(state, object, value);
    }
}
